/**
 * <size-guide-video> — the capacity guide's "what fits in the bag" film (SAVETTE-65).
 *
 * The video is muted, looping and inline with no native controls; its one control is the small
 * round button in its corner, which plays and pauses it and says which it will do in its label.
 * The video keeps preload="none", so nothing downloads with the product page: it starts when the
 * drawer opens and stops when the drawer closes. A shopper who asks for reduced motion gets it
 * paused, with the play button, and it only plays when they press it. A pause the shopper chose
 * is kept the next time the drawer opens.
 *
 * Without this script the button stays hidden and the poster stands alone.
 */
class SizeGuideVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('.size-guide__toggle');
    if (!this.video || !this.button) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.drawer = this.closest('site-drawer');

    this.video.addEventListener('play', this.render);
    this.video.addEventListener('pause', this.render);
    this.button.addEventListener('click', this.onToggle);
    this.drawer?.addEventListener('drawer:open', this.onOpen);
    this.drawer?.addEventListener('drawer:close', this.onClose);

    // video_tag adds no controls unless asked and the markup asks for muted; both are set here
    // too, since a film that plays itself must be silent and the button is its only control
    this.video.muted = true;
    this.video.controls = false;
    this.button.hidden = false;
    this.render();
    // the drawer may already be open if this script arrived late
    if (this.drawer && !this.drawer.hidden) this.onOpen();
  }

  disconnectedCallback() {
    this.video?.removeEventListener('play', this.render);
    this.video?.removeEventListener('pause', this.render);
    this.button?.removeEventListener('click', this.onToggle);
    this.drawer?.removeEventListener('drawer:open', this.onOpen);
    this.drawer?.removeEventListener('drawer:close', this.onClose);
  }

  onOpen = () => {
    if (this.reducedMotion.matches || this.pausedByShopper) return;
    this.play();
  };

  onClose = () => {
    this.video.pause();
  };

  onToggle = () => {
    if (this.video.paused) {
      this.pausedByShopper = false;
      this.play();
    } else {
      this.pausedByShopper = true;
      this.video.pause();
    }
  };

  play() {
    // a refused play (a browser that blocks it) leaves the video paused and the play button up
    this.video.play()?.catch(() => this.render());
  }

  render = () => {
    const playing = !this.video.paused;
    this.toggleAttribute('data-playing', playing);
    const label = playing ? this.button.dataset.labelPause : this.button.dataset.labelPlay;
    if (label) this.button.setAttribute('aria-label', label);
  };
}

if (!customElements.get('size-guide-video')) customElements.define('size-guide-video', SizeGuideVideo);
